import { inject, Service } from '@angular/core';
import { Router } from '@angular/router';
import { OIP_ROLES, CORP_ROLES, SUB_ROLES, CLIENT_ROLES, REDIRECT_URL_KEY } from '../constants/auth';

/**
 * Handles post-login navigation by routing the user to the correct
 * landing page based on their custom:role Cognito attribute.
 *
 * Known roles navigate to `/`; `roleLandingGuard` (unified app) then redirects to
 * the right area. **Corporate (NBD) roles land on `/oip` — corporate is a role
 * inside the staff app, not a separate area (ADR-018), so there is no `/corp` target.**
 */
@Service()
export class UserNavService {
  readonly #router = inject(Router);

  navigateAfterLogin(role: string): void {
    const redirectUrl = localStorage.getItem(REDIRECT_URL_KEY);
    localStorage.removeItem(REDIRECT_URL_KEY);

    if (redirectUrl) {
      // The one navigation here worth catching. `redirectUrl` is whatever URL the user was denied
      // before signing in, so a guard or resolver on it can REJECT (a plain block resolves `false`)
      // — and an unhandled rejection would leave a signed-in user sitting on the login screen with
      // nothing said. Fall through to the role landing instead.
      this.#router.navigateByUrl(redirectUrl).catch(() => this.#landOnRole(role));
      return;
    }

    this.#landOnRole(role);
  }

  /**
   * Navigate to root — `roleLandingGuard` routes to the correct role area from there — or back to
   * login when the token carries a role we do not ship.
   */
  #landOnRole(role: string): void {
    const knownRole =
      (OIP_ROLES as readonly string[]).includes(role) ||
      (CORP_ROLES as readonly string[]).includes(role) ||
      (SUB_ROLES as readonly string[]).includes(role) ||
      (CLIENT_ROLES as readonly string[]).includes(role);

    void this.#router.navigate(knownRole ? ['/'] : ['/auth/login']);
  }

  /** Route to the TOTP enrollment page (internal staff whose token lacks an MFA factor). */
  navigateToMfaSetup(): void {
    void this.#router.navigate(['/auth/mfa-setup']);
  }

  /** Route back to the sign-in page (e.g. after MFA enrollment, to re-authenticate). */
  navigateToLogin(): void {
    void this.#router.navigate(['/auth/login']);
  }

  /** Route to the TOTP code page when Cognito returns a SOFTWARE_TOKEN_MFA challenge. */
  navigateToVerify2fa(): void {
    void this.#router.navigate(['/auth/verify-2fa']);
  }
}
